import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  Radio,
  Row,
  Select,
  Space,
  Typography,
  message,
} from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { adminAPI, covariateAPI } from '../services/api';
import { getDrugLabel } from '../constants/drugs';
import { formatErrorMessage } from '../utils/errorHandler';
import { CONCENTRATION_UNIT_OPTIONS } from '../utils/quantityUnits';

const { Text } = Typography;

interface TruthModelOption {
  model_id: string;
  drug?: string;
  drug_name?: string;
  drug_label?: string;
  first_author?: string;
  publication_year?: number;
  model_units?: { concentration?: string };
}

interface CovariateRow {
  key: string;
  distribution: 'fixed' | 'uniform' | 'normal' | 'lognormal' | 'literature_quantiles' | 'categorical' | 'derived';
  value?: string | number;
  mean?: number;
  median?: number;
  q1?: number;
  q3?: number;
  sd?: number;
  log_sd?: number;
  min?: number;
  max?: number;
  values_text?: string;
  weights_text?: string;
  round_digits?: number;
}

export interface RegisteredCovariateDefinition {
  key: string;
  label: string;
  label_en?: string | null;
  unit?: string | null;
  data_type?: string | null;
  group_name?: string | null;
}

interface CovariateSelectOption {
  value: string;
  label: string;
}

interface SyntheticFormValues {
  name: string;
  truth_model_id: string;
  description?: string;
  n_subjects: number;
  seed: number;
  covariates: CovariateRow[];
  dose_mode: 'mg' | 'mg_kg';
  dose_mg?: number;
  dose_mg_kg?: number;
  interval_h: number;
  infusion_h: number;
  n_doses: number;
  sample_times_text: string;
  sampling_unit: string;
  jitter_sd_h: number;
  missing_rate: number;
  error_source: 'model' | 'custom' | 'none';
  proportional_sd: number;
  additive_sd: number;
  between_subject_log_sd: number;
}

interface SyntheticValidationDatasetModalProps {
  open: boolean;
  models: TruthModelOption[];
  onCancel: () => void;
  onCreated: () => Promise<void> | void;
}

const DISTRIBUTION_OPTIONS = [
  { value: 'fixed', label: '固定值' },
  { value: 'uniform', label: '均匀分布' },
  { value: 'normal', label: '正态分布' },
  { value: 'lognormal', label: '对数正态' },
  { value: 'literature_quantiles', label: '文献分位数' },
  { value: 'categorical', label: '分类比例' },
  { value: 'derived', label: '派生规则' },
];

const CompactField: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <Text type="secondary" style={{ display: 'block', fontSize: 12, lineHeight: '20px' }}>
      {label}
    </Text>
    {children}
  </div>
);

const DEFAULT_COVARIATES: CovariateRow[] = [
  { key: 'weight', distribution: 'normal', mean: 20, sd: 8, min: 2, max: 60, round_digits: 1 },
  { key: 'age_years', distribution: 'uniform', min: 0.1, max: 17, round_digits: 2 },
  { key: 'age_pma', distribution: 'normal', mean: 200, sd: 120, min: 24, max: 900, round_digits: 1 },
  { key: 'height', distribution: 'normal', mean: 110, sd: 30, min: 40, max: 180, round_digits: 1 },
  { key: 'gender', distribution: 'categorical', values_text: 'male,female', weights_text: '1,1' },
  { key: 'scr', distribution: 'lognormal', median: 0.5, log_sd: 0.3, min: 0.1, max: 2, round_digits: 3 },
  { key: 'crcl', distribution: 'derived', round_digits: 1 },
];

export function buildRegisteredCovariateOptions(
  definitions: RegisteredCovariateDefinition[],
): CovariateSelectOption[] {
  return definitions
    .filter((definition) => Boolean(definition?.key?.trim() && definition?.label?.trim()))
    .map((definition) => {
      const key = definition.key.trim();
      const englishLabel = definition.label_en?.trim();
      const unit = definition.unit?.trim();
      return {
        value: key,
        label: `${definition.label.trim()}${englishLabel ? ` / ${englishLabel}` : ''} · ${key}${unit ? ` · ${unit}` : ''}`,
      };
    });
}

function parseScalar(value: string | number | undefined): string | number | boolean | undefined {
  if (value === undefined || value === '') return undefined;
  if (typeof value === 'number') return value;
  const text = value.trim();
  if (text.toLowerCase() === 'true') return true;
  if (text.toLowerCase() === 'false') return false;
  const numeric = Number(text);
  return text !== '' && Number.isFinite(numeric) ? numeric : text;
}

function parseNumberList(value?: string): number[] | undefined {
  if (!value?.trim()) return undefined;
  const items = value.split(',').map((item) => Number(item.trim()));
  if (items.some((item) => !Number.isFinite(item))) {
    throw new Error('数值列表必须使用逗号分隔');
  }
  return items;
}

export function buildCovariatePayload(rows: CovariateRow[]): Record<string, Record<string, unknown>> {
  const payload: Record<string, Record<string, unknown>> = {};
  rows.forEach((row) => {
    const key = row.key?.trim();
    if (!key) throw new Error('协变量名称不能为空');
    if (payload[key]) throw new Error(`协变量重复：${key}`);
    const spec: Record<string, unknown> = { distribution: row.distribution };
    if (row.distribution === 'fixed') spec.value = parseScalar(row.value);
    if (row.distribution === 'uniform') {
      spec.min = row.min;
      spec.max = row.max;
    }
    if (row.distribution === 'normal') {
      spec.mean = row.mean;
      spec.sd = row.sd;
      spec.min = row.min;
      spec.max = row.max;
    }
    if (row.distribution === 'lognormal') {
      spec.median = row.median;
      spec.log_sd = row.log_sd;
      spec.min = row.min;
      spec.max = row.max;
    }
    if (row.distribution === 'literature_quantiles') {
      spec.min = row.min;
      spec.q1 = row.q1;
      spec.median = row.median;
      spec.q3 = row.q3;
      spec.max = row.max;
    }
    if (row.distribution === 'categorical') {
      spec.values = (row.values_text || '').split(',').map((item) => parseScalar(item.trim())).filter((item) => item !== undefined);
      spec.weights = parseNumberList(row.weights_text);
    }
    if (row.round_digits !== undefined) spec.round_digits = row.round_digits;
    payload[key] = spec;
  });
  return payload;
}

const SyntheticValidationDatasetModal: React.FC<SyntheticValidationDatasetModalProps> = ({
  open,
  models,
  onCancel,
  onCreated,
}) => {
  const [form] = Form.useForm<SyntheticFormValues>();
  const [submitting, setSubmitting] = useState(false);
  const [covariateDefinitions, setCovariateDefinitions] = useState<RegisteredCovariateDefinition[]>([]);
  const [covariatesLoading, setCovariatesLoading] = useState(false);
  const [covariatesError, setCovariatesError] = useState<string>();
  const requestIdRef = useRef<string>();
  const doseMode = Form.useWatch('dose_mode', form);
  const errorSource = Form.useWatch('error_source', form);

  const loadCovariateDefinitions = useCallback(async () => {
    setCovariatesLoading(true);
    setCovariatesError(undefined);
    try {
      const response = await covariateAPI.definitions();
      const definitions = Array.isArray(response?.definitions)
        ? response.definitions.filter((item: unknown): item is RegisteredCovariateDefinition => {
          if (!item || typeof item !== 'object') return false;
          const candidate = item as Record<string, unknown>;
          return typeof candidate.key === 'string' && typeof candidate.label === 'string';
        })
        : [];
      if (!definitions.length) {
        throw new Error('平台没有可用的已注册协变量');
      }
      setCovariateDefinitions(definitions);
    } catch (error) {
      setCovariateDefinitions([]);
      setCovariatesError(formatErrorMessage(error, '加载平台注册协变量失败'));
    } finally {
      setCovariatesLoading(false);
    }
  }, []);

  const covariateOptions = useMemo(
    () => buildRegisteredCovariateOptions(covariateDefinitions),
    [covariateDefinitions],
  );
  const registeredCovariateKeys = useMemo(
    () => new Set(covariateOptions.map((option) => option.value)),
    [covariateOptions],
  );

  const modelOptions = useMemo(() => models.map((model) => {
    const drugName = model.drug_name || model.drug || '';
    const source = [model.first_author, model.publication_year].filter(Boolean).join(' ');
    return {
      value: model.model_id,
      label: `${model.model_id} · ${model.drug_label || getDrugLabel(drugName)}${source ? ` · ${source}` : ''}`,
    };
  }), [models]);

  useEffect(() => {
    if (!open) return;
    void loadCovariateDefinitions();
  }, [loadCovariateDefinitions, open]);

  useEffect(() => {
    if (!open) return;
    requestIdRef.current = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const defaultModel = models[0];
    const today = new Date().toISOString().slice(0, 10);
    form.setFieldsValue({
      name: `虚拟回顾验证数据集 ${today}`,
      truth_model_id: defaultModel?.model_id,
      description: '由指定 truth model、协变量分布、给药方案和采样计划生成。',
      n_subjects: 10,
      seed: 42,
      covariates: DEFAULT_COVARIATES.map((item) => ({ ...item })),
      dose_mode: 'mg_kg',
      dose_mg_kg: 15,
      interval_h: 8,
      infusion_h: 1,
      n_doses: 3,
      sample_times_text: '7.5,15.5',
      sampling_unit: defaultModel?.model_units?.concentration || 'mg/L',
      jitter_sd_h: 0,
      missing_rate: 0,
      error_source: 'model',
      proportional_sd: 0.15,
      additive_sd: 0.2,
      between_subject_log_sd: 0,
    });
  }, [form, models, open]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const sampleTimes = parseNumberList(values.sample_times_text);
      if (!sampleTimes?.length) throw new Error('至少需要一个采样时间');
      setSubmitting(true);
      await adminAPI.generateValidationSyntheticDataset({
        name: values.name,
        truth_model_id: values.truth_model_id,
        description: values.description,
        n_subjects: values.n_subjects,
        seed: values.seed,
        request_id: requestIdRef.current,
        covariates: buildCovariatePayload(values.covariates || []),
        dosing: {
          ...(values.dose_mode === 'mg' ? { dose_mg: values.dose_mg } : { dose_mg_kg: values.dose_mg_kg }),
          interval_h: values.interval_h,
          infusion_h: values.infusion_h,
          n_doses: values.n_doses,
          route: 'iv',
        },
        sampling: {
          sample_times_h: sampleTimes,
          jitter_sd_h: values.jitter_sd_h,
          missing_rate: values.missing_rate,
          sample_label: 'synthetic',
          unit: values.sampling_unit,
        },
        concentration_error: {
          source: values.error_source,
          ...(values.error_source === 'custom' ? {
            proportional_sd: values.proportional_sd,
            additive_sd: values.additive_sd,
          } : {}),
          between_subject_log_sd: values.between_subject_log_sd,
          lower_limit: 0,
          round_digits: 4,
        },
      });
      message.success('虚拟验证数据集已生成');
      onCancel();
      await onCreated();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '';
      if (errorMessage.toLowerCase().includes('timeout')) {
        message.warning('请求等待超时，后端可能仍在生成。请先刷新数据集列表，确认没有生成成功后再重试。');
      } else {
        message.error(formatErrorMessage(error, errorMessage || '生成虚拟数据集失败'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title="生成虚拟验证数据集"
      open={open}
      onCancel={onCancel}
      onOk={handleSubmit}
      confirmLoading={submitting}
      width={1120}
      okText={submitting ? '生成中…' : '生成'}
      okButtonProps={{ disabled: covariatesLoading || Boolean(covariatesError) }}
      destroyOnHidden
    >
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
        message="虚拟数据仅用于技术验证，不会写入模型的外部验证状态。每位受试者都会从 Truth model 的 OMEGA 抽取 ETA，再以个体参数运行 rxSolve；默认用该模型的 SIGMA/error_model 生成测量误差。AGE 与 PMA 会按各自分布采样；需要严格一致时，应改用 GA、PNA 并将 PMA 配置为派生。"
      />
      <Form form={form} layout="vertical">
        <Row gutter={12}>
          <Col xs={24} lg={12}>
            <Form.Item name="truth_model_id" label="真值模型" rules={[{ required: true }]}>
              <Select
                showSearch
                optionFilterProp="label"
                options={modelOptions}
                onChange={(modelId) => {
                  const model = models.find((item) => item.model_id === modelId);
                  form.setFieldValue('sampling_unit', model?.model_units?.concentration || 'mg/L');
                }}
              />
            </Form.Item>
          </Col>
          <Col xs={24} lg={12}>
            <Form.Item name="name" label="数据集名称" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col xs={24} lg={12}>
            <Form.Item name="description" label="说明">
              <Input />
            </Form.Item>
          </Col>
          <Col xs={12} lg={6}>
            <Form.Item name="n_subjects" label="受试者数" rules={[{ required: true }]}>
              <InputNumber min={1} max={1000} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={12} lg={6}>
            <Form.Item name="seed" label="随机种子" rules={[{ required: true }]}>
              <InputNumber min={0} max={2147483647} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>

        <Space direction="vertical" size={8} style={{ width: '100%', marginBottom: 16 }}>
          <Text strong>协变量分布</Text>
          <Text type="secondary">
            协变量名称来自平台协变量注册表；可按中文名、英文名、标准 key 或单位检索。SCr 未另行指定单位时按平台注册标准单位 mg/dL 处理。
          </Text>
          {covariatesError ? (
            <Alert
              type="error"
              showIcon
              message={covariatesError}
              action={<Button size="small" onClick={() => void loadCovariateDefinitions()}>重新加载</Button>}
            />
          ) : null}
          <Form.List name="covariates">
            {(fields, { add, remove }) => (
              <Space direction="vertical" size={8} style={{ width: '100%' }}>
                {fields.map((field) => (
                  <Row gutter={8} key={field.key} align="top">
                    <Col span={4}>
                      <CompactField label="协变量">
                        <Form.Item
                          name={[field.name, 'key']}
                          rules={[
                            { required: true, message: '请选择协变量' },
                            {
                              validator: (_, value) => (
                                !value || registeredCovariateKeys.has(value)
                                  ? Promise.resolve()
                                  : Promise.reject(new Error('请选择平台注册的标准协变量'))
                              ),
                            },
                          ]}
                          style={{ marginBottom: 0 }}
                        >
                          <Select
                            showSearch
                            allowClear
                            loading={covariatesLoading}
                            optionFilterProp="label"
                            options={covariateOptions}
                            placeholder="检索标准协变量"
                            notFoundContent={covariatesLoading ? '正在加载…' : '没有匹配的注册协变量'}
                          />
                        </Form.Item>
                      </CompactField>
                    </Col>
                    <Col span={4}>
                      <CompactField label="分布">
                        <Form.Item name={[field.name, 'distribution']} rules={[{ required: true }]} style={{ marginBottom: 0 }}>
                          <Select options={DISTRIBUTION_OPTIONS} />
                        </Form.Item>
                      </CompactField>
                    </Col>
                    <Form.Item noStyle shouldUpdate>
                      {() => {
                        const distribution = form.getFieldValue(['covariates', field.name, 'distribution']);
                        if (distribution === 'derived') return (
                          <Col span={14}>
                            <CompactField label="派生方式">
                              <Text type="secondary" style={{ lineHeight: '32px' }}>使用数据库派生规则</Text>
                            </CompactField>
                          </Col>
                        );
                        if (distribution === 'categorical') {
                          return (
                            <>
                              <Col span={7}>
                                <CompactField label="分类取值">
                                  <Form.Item name={[field.name, 'values_text']} style={{ marginBottom: 0 }}>
                                    <Input placeholder="male,female" />
                                  </Form.Item>
                                </CompactField>
                              </Col>
                              <Col span={7}>
                                <CompactField label="分类权重">
                                  <Form.Item name={[field.name, 'weights_text']} style={{ marginBottom: 0 }}>
                                    <Input placeholder="1,1" />
                                  </Form.Item>
                                </CompactField>
                              </Col>
                            </>
                          );
                        }
                        if (distribution === 'fixed') {
                          return (
                            <Col span={14}>
                              <CompactField label="固定值">
                                <Form.Item name={[field.name, 'value']} style={{ marginBottom: 0 }}>
                                  <Input />
                                </Form.Item>
                              </CompactField>
                            </Col>
                          );
                        }
                        if (distribution === 'literature_quantiles') {
                          return (
                            <Col span={14}>
                              <Row gutter={6}>
                                <Col span={4}>
                                  <CompactField label="最小值">
                                    <Form.Item name={[field.name, 'min']} style={{ marginBottom: 0 }}>
                                      <InputNumber style={{ width: '100%' }} />
                                    </Form.Item>
                                  </CompactField>
                                </Col>
                                <Col span={4}>
                                  <CompactField label="Q1 (25%)">
                                    <Form.Item name={[field.name, 'q1']} style={{ marginBottom: 0 }}>
                                      <InputNumber style={{ width: '100%' }} />
                                    </Form.Item>
                                  </CompactField>
                                </Col>
                                <Col span={4}>
                                  <CompactField label="中位数">
                                    <Form.Item name={[field.name, 'median']} style={{ marginBottom: 0 }}>
                                      <InputNumber style={{ width: '100%' }} />
                                    </Form.Item>
                                  </CompactField>
                                </Col>
                                <Col span={4}>
                                  <CompactField label="Q3 (75%)">
                                    <Form.Item name={[field.name, 'q3']} style={{ marginBottom: 0 }}>
                                      <InputNumber style={{ width: '100%' }} />
                                    </Form.Item>
                                  </CompactField>
                                </Col>
                                <Col span={4}>
                                  <CompactField label="最大值">
                                    <Form.Item name={[field.name, 'max']} style={{ marginBottom: 0 }}>
                                      <InputNumber style={{ width: '100%' }} />
                                    </Form.Item>
                                  </CompactField>
                                </Col>
                                <Col span={4}>
                                  <CompactField label="小数位">
                                    <Form.Item name={[field.name, 'round_digits']} style={{ marginBottom: 0 }}>
                                      <InputNumber min={0} max={8} style={{ width: '100%' }} />
                                    </Form.Item>
                                  </CompactField>
                                </Col>
                              </Row>
                            </Col>
                          );
                        }
                        const centerField = distribution === 'lognormal' ? 'median' : 'mean';
                        const spreadField = distribution === 'lognormal' ? 'log_sd' : 'sd';
                        return (
                          <>
                            {distribution !== 'uniform' ? (
                              <Col span={4}>
                                <CompactField label={centerField === 'median' ? '中位数' : '均值'}>
                                  <Form.Item name={[field.name, centerField]} style={{ marginBottom: 0 }}>
                                    <InputNumber style={{ width: '100%' }} />
                                  </Form.Item>
                                </CompactField>
                              </Col>
                            ) : null}
                            {distribution !== 'uniform' ? (
                              <Col span={3}>
                                <CompactField label={spreadField === 'log_sd' ? 'log SD' : '标准差 SD'}>
                                  <Form.Item name={[field.name, spreadField]} style={{ marginBottom: 0 }}>
                                    <InputNumber min={0} style={{ width: '100%' }} />
                                  </Form.Item>
                                </CompactField>
                              </Col>
                            ) : null}
                            <Col span={distribution === 'uniform' ? 7 : 3}>
                              <CompactField label="最小值">
                                <Form.Item name={[field.name, 'min']} style={{ marginBottom: 0 }}>
                                  <InputNumber style={{ width: '100%' }} />
                                </Form.Item>
                              </CompactField>
                            </Col>
                            <Col span={distribution === 'uniform' ? 7 : 4}>
                              <CompactField label="最大值">
                                <Form.Item name={[field.name, 'max']} style={{ marginBottom: 0 }}>
                                  <InputNumber style={{ width: '100%' }} />
                                </Form.Item>
                              </CompactField>
                            </Col>
                          </>
                        );
                      }}
                    </Form.Item>
                    <Col span={2} style={{ paddingTop: 20 }}>
                      <Button icon={<DeleteOutlined />} danger onClick={() => remove(field.name)} aria-label="删除协变量" />
                    </Col>
                  </Row>
                ))}
                <Button icon={<PlusOutlined />} onClick={() => add({ distribution: 'fixed' })}>添加协变量</Button>
              </Space>
            )}
          </Form.List>
        </Space>

        <Row gutter={12}>
          <Col xs={24} lg={6}>
            <Form.Item name="dose_mode" label="剂量方式">
              <Radio.Group optionType="button" buttonStyle="solid">
                <Radio.Button value="mg_kg">mg/kg</Radio.Button>
                <Radio.Button value="mg">固定 mg</Radio.Button>
              </Radio.Group>
            </Form.Item>
          </Col>
          <Col xs={12} lg={4}>
            <Form.Item name={doseMode === 'mg' ? 'dose_mg' : 'dose_mg_kg'} label={doseMode === 'mg' ? '单次剂量 mg' : '单次剂量 mg/kg'} rules={[{ required: true }]}>
              <InputNumber min={0.01} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={12} lg={4}>
            <Form.Item name="interval_h" label="间隔 h" rules={[{ required: true }]}>
              <InputNumber min={0.1} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={12} lg={4}>
            <Form.Item name="infusion_h" label="输注 h" rules={[{ required: true }]}>
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={12} lg={3}>
            <Form.Item name="n_doses" label="剂次数" rules={[{ required: true }]}>
              <InputNumber min={1} max={100} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col xs={24} lg={8}>
            <Form.Item name="sample_times_text" label="采样时间 h" rules={[{ required: true }]}>
              <Input placeholder="7.5,15.5" />
            </Form.Item>
          </Col>
          <Col xs={12} lg={4}>
            <Form.Item name="jitter_sd_h" label="时间扰动 SD h">
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={12} lg={4}>
            <Form.Item name="missing_rate" label="缺失比例">
              <InputNumber min={0} max={0.99} step={0.05} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={12} lg={4}>
            <Form.Item
              name="sampling_unit"
              label="观测浓度单位"
              tooltip="默认采用 Truth model 的原生浓度单位；后端会在该单位下生成观测误差。"
              rules={[{ required: true }]}
            >
              <Select options={CONCENTRATION_UNIT_OPTIONS} />
            </Form.Item>
          </Col>
          <Col xs={24} lg={4}>
            <Form.Item
              name="error_source"
              label="测量误差来源"
              tooltip="推荐使用 Truth model 的 SIGMA 和 error_model；自定义模式用于复现文献表格中的误差设定。"
            >
              <Select options={[
                { value: 'model', label: 'Truth model SIGMA（推荐）' },
                { value: 'custom', label: '自定义比例 + 加和误差' },
                { value: 'none', label: '无测量误差' },
              ]} />
            </Form.Item>
          </Col>
          {errorSource === 'custom' ? (
            <>
              <Col xs={12} lg={3}>
                <Form.Item name="proportional_sd" label="比例误差 SD">
                  <InputNumber min={0} step={0.05} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={12} lg={3}>
                <Form.Item name="additive_sd" label="加和误差 SD">
                  <InputNumber min={0} step={0.1} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </>
          ) : null}
          <Col xs={24} lg={errorSource === 'custom' ? 3 : 8}>
            <Form.Item
              name="between_subject_log_sd"
              label="额外整体缩放 log-SD"
              tooltip="OMEGA 已负责参数层面的个体差异。此项仅用于额外、非模型化的整体偏移；通常保持 0。"
            >
              <InputNumber min={0} step={0.05} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default SyntheticValidationDatasetModal;
